import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import Spinner from '@ui/Spinner';
import {
  deleteComment,
  getBlogComments,
  postComment,
} from '@util/firestore/blog';
import { timeAgo } from '@util/index';
import {
  BlogCommentDocument,
  blogCommentDocumentSchema,
} from '@util/types/firestore/blog';
import { useAuth } from 'context/AuthContext';
import { useState } from 'react';

const BlogCommentSection = ({ blogId }: { blogId: string }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['blogComments', blogId, null],
    queryFn: () => getBlogComments({ blogId, parentId: null }),
  });
  if (isLoading)
    return (
      <div className="mx-auto mt-[4rem] h-[8rem] w-[8rem]">
        <Spinner />
      </div>
    );
  if (!data) return <div>error</div>;
  const comments = data.results ?? [];
  const commentCount = comments.length;
  return (
    <div className="mt-[4rem]">
      {/* title */}
      <div className="flex">
        <ChatsIcon />
        <h3 className="ml-[1.6rem] text-[2.4rem] font-semibold">
          Comments ({commentCount})
        </h3>
      </div>

      {/* comment form */}
      <CommentInput commentType="comment" blogId={blogId} parentId={null} />

      {/* comments */}
      <div className="mt-[2rem]">
        {comments.map((c, i: number) => (
          <BlogComment key={i} comment={c} blogId={blogId} depth={0} />
        ))}
      </div>
    </div>
  );
};

export default BlogCommentSection;

type BlogCommentProps = {
  comment: BlogCommentDocument;
  blogId: string;
  depth: number;
};

const BlogComment = ({ comment, blogId, depth }: BlogCommentProps) => {
  const [reply, setReply] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['blogComments', blogId, comment.comment_id],
    queryFn: () => getBlogComments({ blogId, parentId: comment.comment_id }),
  });
  const replies = data?.results ?? [];
  const deleteCommentMutation = useMutation({
    mutationFn: deleteComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['blogComments', blogId, comment.parent_id],
      });
    },
  });
  const handleDeleteClick = () => {
    deleteCommentMutation.mutate({ commentId: comment.comment_id });
  };
  const hideReplies = depth > 3;
  return (
    <>
      <hr className="mt-[1.6rem]" />
      <div
        className={`flex items-start gap-[1.6rem] pt-[1.6rem] ${
          depth === 0 ? 'overflow-scroll' : ''
        }`}
      >
        <SafeImage
          alt={'pfp image'}
          src={comment.pfp_url}
          width={44}
          height={44}
          key={comment.pfp_url}
          className="rounded-full"
        />
        <div className="grow">
          {/* title */}
          <p className="font-semibold">
            {comment.author_name}
            <span className="ml-[.8rem] text-[1.3rem] font-normal text-brand-gray">
              {timeAgo(comment.timestamp)}
            </span>
          </p>
          {/* comment */}
          <p className="text-brand-black">{comment.comment}</p>
          {/* buttons */}
          <div>
            <button
              className="mt-[.8rem] font-semibold text-brand-secondary"
              onClick={() => setReply(!reply)}
            >
              Reply
            </button>
            {!!user && comment.author_id === user.uid && (
              <button
                className="ml-[1.6rem] mt-[.8rem] font-semibold text-brand-primary"
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            )}
          </div>

          {/* user reply */}
          {reply && (
            <CommentInput
              placeholder={`Reply to ${comment.author_name}`}
              commentType="reply"
              blogId={blogId}
              parentId={comment.comment_id}
              onCommentSuccess={() => setReply(false)}
            />
          )}

          {/* threads */}
          {hideReplies && replies.length > 0 ? (
            <button className="mt-4 w-full font-medium text-brand-secondary">
              show more
            </button>
          ) : (
            replies?.map((reply) => (
              <BlogComment
                key={reply.comment_id}
                comment={reply}
                blogId={blogId}
                depth={depth + 1}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

const CommentInput = ({
  placeholder = 'Leave a comment',
  commentType,
  blogId,
  parentId = null,
  onCommentSuccess,
}: {
  placeholder?: string;
  commentType: 'reply' | 'comment';
  blogId: string; // blogId or commentId
  parentId: string | null;
  onCommentSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { userDoc, user } = useAuth();
  const [input, setInput] = useState('');
  const postCommentMutation = useMutation({
    mutationFn: postComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['blogComments', blogId, parentId],
      });
      setInput('');
      onCommentSuccess?.();
    },
  });

  const handlePostClick = () => {
    if (!input || !userDoc) return;
    const doc = blogCommentDocumentSchema.parse({
      author_id: userDoc.uid,
      author_name: userDoc.name,
      blog_id: blogId,
      comment: input,
      comment_id: '',
      pfp_url: userDoc.photo,
      timestamp: Date.now(),
      reply_ids: [],
      parent_id: parentId,
    });
    postCommentMutation.mutate(doc);
  };
  return (
    <>
      <hr className="mt-[2rem]" />
      {/* logged out */}
      {!user && (
        <div className="mx-auto flex flex-col items-center gap-[1.6rem] p-[2.4rem]">
          <PadlockIcon />
          <p>You must login first to leave a comment</p>
          <div className="max-w-[14rem]">
            <Button type="secondary" href="/login" text="Login Now" />
          </div>
        </div>
      )}
      {/* logged in */}
      {!!userDoc && (
        <div className="mt-[2.4rem] flex flex-col gap-[1.6rem]">
          <div className="flex items-start gap-[1.6rem]">
            <SafeImage
              src={userDoc.photo}
              alt="pfp image"
              height={44}
              width={44}
              className="hidden rounded-full sm:block"
            />
            <textarea
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              value={input}
              className="focus:outline-primary block min-h-[10rem] w-full resize-none rounded-[1.6rem] bg-brand-darker-white p-[1.6rem]"
            />
          </div>
          <div className="w-fit self-end">
            <Button
              loading={postCommentMutation.isPending}
              disabled={!input}
              type="secondary"
              text={commentType === 'comment' ? 'Post Comment' : 'Post Reply'}
              onClick={handlePostClick}
            />
          </div>
        </div>
      )}
    </>
  );
};

const ChatsIcon = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.95 18L4 22V6C4 5.73478 4.10536 5.48043 4.29289 5.29289C4.48043 5.10536 4.73478 5 5 5H21C21.2652 5 21.5196 5.10536 21.7071 5.29289C21.8946 5.48043 22 5.73478 22 6V17C22 17.2652 21.8946 17.5196 21.7071 17.7071C21.5196 17.8946 21.2652 18 21 18H8.95Z"
        stroke="#808080"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 18V23C10 23.2652 10.1054 23.5196 10.2929 23.7071C10.4804 23.8946 10.7348 24 11 24H23.05L28 28V12C28 11.7348 27.8946 11.4804 27.7071 11.2929C27.5196 11.1054 27.2652 11 27 11H22"
        stroke="#808080"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const PadlockIcon = () => {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask id="mask0_1413_1642" x="0" y="0" width="44" height="44">
        <path d="M0 0H44V44H0V0Z" fill="white" />
      </mask>
      <g mask="url(#mask0_1413_1642)">
        <path
          d="M42.7109 22C42.7109 33.4384 33.4383 42.7109 22 42.7109C10.5615 42.7109 1.28906 33.4384 1.28906 22C1.28906 10.5615 10.5615 1.28906 22 1.28906C33.4383 1.28906 42.7109 10.5615 42.7109 22Z"
          stroke="black"
          strokeWidth="1.5"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M30.2842 19.2391C30.2842 23.8144 26.5753 27.5234 21.9998 27.5234C17.4241 27.5234 13.7153 23.8144 13.7153 19.2391C13.7153 14.6638 17.4241 10.9548 21.9998 10.9548C26.5753 10.9548 30.2842 14.6638 30.2842 19.2391Z"
          fill="#C41719"
          stroke="black"
          strokeWidth="1.5"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.64307 38.6229C10.3056 32.3836 15.585 27.5234 21.9998 27.5234C28.4147 27.5234 33.694 32.384 34.3563 38.623"
          stroke="black"
          strokeWidth="1.5"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};
